'use client';

// Client island for the only two industry sections that need real
// interactivity: tab switching and the gallery carousel. Everything else
// in IndustryPageSections.tsx stays server-rendered for SSR/SEO.
import { c as _c } from "react/compiler-runtime";
import useEmblaCarousel from 'embla-carousel-react';
import { useState } from 'react';
import type { MouseEvent } from 'react';
import { Check } from 'lucide-react';
import { CmsImage, CtaLink, type IndustryGalleryContent, type IndustryTabbedContent, type IndustryTabbedSectionVariant, type IndustryTabContent } from '../IndustryPrimitives';
import styles from '../IndustryPageSections/IndustryPageSections.module.css';
function firstTabId(tabs: IndustryTabContent[]) {
  const firstTab = tabs[0];
  if (!firstTab) {
    throw new Error('Industry tab group cannot be empty.');
  }
  return firstTab.id;
}
function TabList(t0) {
  const $ = _c(12);
  const {
    tabs,
    activeId,
    onSelect,
    field
  } = t0;
  let t1;
  if ($[0] !== onSelect) {
    t1 = (event, id) => {
      event.preventDefault();
      onSelect(id);
    };
    $[0] = onSelect;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const handleSelect = t1;
  let t2;
  if ($[2] !== activeId || $[3] !== handleSelect || $[4] !== tabs) {
    let t3;
    if ($[6] !== activeId || $[7] !== handleSelect) {
      t3 = tab => <li key={tab.id} className={styles.tabItem}><a href={tab.href} className={`${styles.tabLink} ${activeId === tab.id ? styles.tabLinkActive : ""}`} id={`${tab.id}-tab`} data-toggle="tab" role="tab" aria-controls={tab.id} aria-selected={activeId === tab.id} onClick={event_0 => handleSelect(event_0, tab.id)}>{tab.label}</a></li>;
      $[6] = activeId;
      $[7] = handleSelect;
      $[8] = t3;
    } else {
      t3 = $[8];
    }
    t2 = tabs.map(t3);
    $[2] = activeId;
    $[3] = handleSelect;
    $[4] = tabs;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[9] !== field || $[10] !== t2) {
    t3 = <div className={styles.tabScroller}><ul className={styles.tabList} role="tablist" data-cms-field={field}>{t2}</ul></div>;
    $[9] = field;
    $[10] = t2;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  return t3;
}
function BulletList(t0) {
  const $ = _c(4);
  const {
    bullets
  } = t0;
  let t1;
  if ($[0] !== bullets) {
    t1 = bullets.map(_temp);
    $[0] = bullets;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t1) {
    t2 = <ul className={styles.bulletList}>{t1}</ul>;
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  return t2;
}
function _temp(bullet) {
  return <li key={bullet}><Check className={styles.checkIcon} strokeWidth={1.7} /><span>{bullet}</span></li>;
}
function LayeredPanel(t0) {
  const $ = _c(36);
  const {
    tab,
    active,
    ctaHref
  } = t0;
  const t1 = `${styles.tabPanel} ${active ? styles.tabPanelActive : ""}`;
  const t2 = `${tab.id}-tab`;
  const t3 = !active;
  const t4 = !active;
  const t5 = active ? "true" : "false";
  let t6;
  if ($[0] !== tab.heading) {
    t6 = <h3 className={styles.panelTitle}>{tab.heading}</h3>;
    $[0] = tab.heading;
    $[1] = t6;
  } else {
    t6 = $[1];
  }
  let t7;
  if ($[2] !== tab.bullets) {
    t7 = <BulletList bullets={tab.bullets} />;
    $[2] = tab.bullets;
    $[3] = t7;
  } else {
    t7 = $[3];
  }
  let t8;
  if ($[4] !== tab.secondaryBullets || $[5] !== tab.secondaryHeading) {
    t8 = tab.secondaryHeading && <><h3 className={styles.panelTitle}>{tab.secondaryHeading}</h3><BulletList bullets={tab.secondaryBullets ?? []} /></>;
    $[4] = tab.secondaryBullets;
    $[5] = tab.secondaryHeading;
    $[6] = t8;
  } else {
    t8 = $[6];
  }
  let t9;
  if ($[7] !== ctaHref) {
    t9 = <CtaLink href={ctaHref}>Get in Touch</CtaLink>;
    $[7] = ctaHref;
    $[8] = t9;
  } else {
    t9 = $[8];
  }
  let t10;
  if ($[9] !== t6 || $[10] !== t7 || $[11] !== t8 || $[12] !== t9) {
    t10 = <div className={styles.panelCopy}>{t6}{t7}{t8}{t9}</div>;
    $[9] = t6;
    $[10] = t7;
    $[11] = t8;
    $[12] = t9;
    $[13] = t10;
  } else {
    t10 = $[13];
  }
  const t11 = active ? "high" : "low";
  let t12;
  if ($[14] !== t11 || $[15] !== tab.image) {
    t12 = <CmsImage image={tab.image} className={styles.figureBackground} loading="eager" fetchPriority={t11} sizes="(min-width: 1024px) 54vw, 100vw" />;
    $[14] = t11;
    $[15] = tab.image;
    $[16] = t12;
  } else {
    t12 = $[16];
  }
  let t13;
  if ($[17] !== tab.hoverImage) {
    t13 = tab.hoverImage && <CmsImage image={tab.hoverImage} className={styles.figureHoverImage} loading="eager" fetchPriority="low" sizes="(min-width: 1024px) 34vw, 80vw" />;
    $[17] = tab.hoverImage;
    $[18] = t13;
  } else {
    t13 = $[18];
  }
  let t14;
  if ($[19] !== tab.viewMoreHref) {
    t14 = tab.viewMoreHref && <figcaption className={styles.figureCaption}><a href={tab.viewMoreHref}>View more</a></figcaption>;
    $[19] = tab.viewMoreHref;
    $[20] = t14;
  } else {
    t14 = $[20];
  }
  let t15;
  if ($[21] !== t12 || $[22] !== t13 || $[23] !== t14) {
    t15 = <figure className={styles.hoverFigure}>{t12}{t13}{t14}</figure>;
    $[21] = t12;
    $[22] = t13;
    $[23] = t14;
    $[24] = t15;
  } else {
    t15 = $[24];
  }
  let t16;
  if ($[25] !== t10 || $[26] !== t15) {
    t16 = <div className={styles.panelGrid}>{t10}{t15}</div>;
    $[25] = t10;
    $[26] = t15;
    $[27] = t16;
  } else {
    t16 = $[27];
  }
  let t17;
  if ($[28] !== t1 || $[29] !== t16 || $[30] !== t2 || $[31] !== t3 || $[32] !== t4 || $[33] !== t5 || $[34] !== tab.id) {
    t17 = <div id={tab.id} className={t1} role="tabpanel" aria-labelledby={t2} aria-hidden={t3} inert={t4} data-active={t5}>{t16}</div>;
    $[28] = t1;
    $[29] = t16;
    $[30] = t2;
    $[31] = t3;
    $[32] = t4;
    $[33] = t5;
    $[34] = tab.id;
    $[35] = t17;
  } else {
    t17 = $[35];
  }
  return t17;
}
function ChoosePanel(t0) {
  const $ = _c(24);
  const {
    tab,
    active,
    ctaHref
  } = t0;
  const t1 = `${styles.tabPanel} ${active ? styles.tabPanelActive : ""}`;
  const t2 = `${tab.id}-tab`;
  const t3 = !active;
  const t4 = !active;
  const t5 = active ? "true" : "false";
  let t6;
  if ($[0] !== tab.heading) {
    t6 = <h3 className={styles.panelTitle}>{tab.heading}</h3>;
    $[0] = tab.heading;
    $[1] = t6;
  } else {
    t6 = $[1];
  }
  let t7;
  if ($[2] !== tab.bullets) {
    t7 = <BulletList bullets={tab.bullets} />;
    $[2] = tab.bullets;
    $[3] = t7;
  } else {
    t7 = $[3];
  }
  let t8;
  if ($[4] !== ctaHref) {
    t8 = <CtaLink href={ctaHref}>Get in Touch</CtaLink>;
    $[4] = ctaHref;
    $[5] = t8;
  } else {
    t8 = $[5];
  }
  let t9;
  if ($[6] !== t6 || $[7] !== t7 || $[8] !== t8) {
    t9 = <div className={styles.panelCopy}>{t6}{t7}{t8}</div>;
    $[6] = t6;
    $[7] = t7;
    $[8] = t8;
    $[9] = t9;
  } else {
    t9 = $[9];
  }
  const t10 = active ? "high" : "low";
  let t11;
  if ($[10] !== t10 || $[11] !== tab.image) {
    t11 = <div className={styles.chooseImageWrap}><CmsImage image={tab.image} className={styles.chooseImage} loading="eager" fetchPriority={t10} sizes="(min-width: 1024px) 50vw, 100vw" /></div>;
    $[10] = t10;
    $[11] = tab.image;
    $[12] = t11;
  } else {
    t11 = $[12];
  }
  let t12;
  if ($[13] !== t11 || $[14] !== t9) {
    t12 = <div className={`${styles.panelGrid} ${styles.choosePanelGrid}`}>{t9}{t11}</div>;
    $[13] = t11;
    $[14] = t9;
    $[15] = t12;
  } else {
    t12 = $[15];
  }
  let t13;
  if ($[16] !== t1 || $[17] !== t12 || $[18] !== t2 || $[19] !== t3 || $[20] !== t4 || $[21] !== t5 || $[22] !== tab.id) {
    t13 = <div id={tab.id} className={t1} role="tabpanel" aria-labelledby={t2} aria-hidden={t3} inert={t4} data-active={t5}>{t12}</div>;
    $[16] = t1;
    $[17] = t12;
    $[18] = t2;
    $[19] = t3;
    $[20] = t4;
    $[21] = t5;
    $[22] = tab.id;
    $[23] = t13;
  } else {
    t13 = $[23];
  }
  return t13;
}
export function IndustryTabbedSection(t0) {
  const $ = _c(33);
  const {
    content,
    ctaHref,
    variant: t1
  } = t0;
  const variant = t1 === undefined ? "layered" : t1;
  let t2;
  if ($[0] !== content.tabs) {
    t2 = () => firstTabId(content.tabs);
    $[0] = content.tabs;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const [activeId, setActiveId] = useState(t2);
  const t3 = content.id ?? (variant === "layered" ? "layered" : undefined);
  const t4 = content.blockId;
  let t5;
  if ($[2] !== content.eyebrow) {
    t5 = <span className={styles.eyebrow} data-cms-field="eyebrow">{content.eyebrow}</span>;
    $[2] = content.eyebrow;
    $[3] = t5;
  } else {
    t5 = $[3];
  }
  let t6;
  if ($[4] !== content.title) {
    t6 = <h2 className={styles.sectionTitle} data-cms-field="title">{content.title}</h2>;
    $[4] = content.title;
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  let t7;
  if ($[6] !== content.body) {
    t7 = <p className={styles.introBody} data-cms-field="body">{content.body}</p>;
    $[6] = content.body;
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  let t8;
  if ($[8] !== activeId || $[9] !== content.tabs) {
    t8 = <TabList tabs={content.tabs} activeId={activeId} onSelect={setActiveId} field="tabs" />;
    $[8] = activeId;
    $[9] = content.tabs;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  const t9 = content.sourceInteraction;
  let t10;
  if ($[11] !== activeId || $[12] !== content.tabs || $[13] !== ctaHref || $[14] !== variant) {
    let t11;
    if ($[16] !== activeId || $[17] !== ctaHref || $[18] !== variant) {
      t11 = tab => variant === "choose" ? <ChoosePanel key={tab.id} tab={tab} active={activeId === tab.id} ctaHref={ctaHref} /> : <LayeredPanel key={tab.id} tab={tab} active={activeId === tab.id} ctaHref={ctaHref} />;
      $[16] = activeId;
      $[17] = ctaHref;
      $[18] = variant;
      $[19] = t11;
    } else {
      t11 = $[19];
    }
    t10 = content.tabs.map(t11);
    $[11] = activeId;
    $[12] = content.tabs;
    $[13] = ctaHref;
    $[14] = variant;
    $[15] = t10;
  } else {
    t10 = $[15];
  }
  let t11;
  if ($[20] !== content.sourceInteraction || $[21] !== t10) {
    t11 = <div className={styles.tabPanels} data-cms-interaction={t9}>{t10}</div>;
    $[20] = content.sourceInteraction;
    $[21] = t10;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  let t12;
  if ($[23] !== t11 || $[24] !== t5 || $[25] !== t6 || $[26] !== t7 || $[27] !== t8) {
    t12 = <div className={styles.container}>{t5}{t6}{t7}{t8}{t11}</div>;
    $[23] = t11;
    $[24] = t5;
    $[25] = t6;
    $[26] = t7;
    $[27] = t8;
    $[28] = t12;
  } else {
    t12 = $[28];
  }
  let t13;
  if ($[29] !== content.blockId || $[30] !== t12 || $[31] !== t3) {
    t13 = <section id={t3} className={styles.tabSection} data-cms-block-id={t4}>{t12}</section>;
    $[29] = content.blockId;
    $[30] = t12;
    $[31] = t3;
    $[32] = t13;
  } else {
    t13 = $[32];
  }
  return t13;
}
export function IndustryGalleryCarousel(t0) {
  const $ = _c(22);
  const {
    content
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      align: "start",
      containScroll: "trimSnaps",
      dragFree: false,
      loop: false
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [emblaRef] = useEmblaCarousel(t1);
  const t2 = content.blockId;
  let t3;
  if ($[1] !== content.title) {
    t3 = <h4 className={styles.galleryHeading} data-cms-field="title">{content.title}</h4>;
    $[1] = content.title;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== content.body) {
    t4 = <p className={styles.bodyText} data-cms-field="body">{content.body}</p>;
    $[3] = content.body;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== t3 || $[6] !== t4) {
    t5 = <div className={styles.container}>{t3}{t4}</div>;
    $[5] = t3;
    $[6] = t4;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const t6 = content.sourceInteraction;
  let t7;
  if ($[8] !== content.cards) {
    t7 = content.cards.map(_temp2);
    $[8] = content.cards;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  let t8;
  if ($[10] !== t7) {
    t8 = <div className={styles.carouselTrack}>{t7}</div>;
    $[10] = t7;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  let t9;
  if ($[12] !== emblaRef || $[13] !== t8) {
    t9 = <div className={styles.carouselViewport} ref={emblaRef}>{t8}</div>;
    $[12] = emblaRef;
    $[13] = t8;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  let t10;
  if ($[15] !== content.sourceInteraction || $[16] !== t9) {
    t10 = <div className={styles.galleryCarousel} data-cms-interaction={t6} data-carousel-library="embla-carousel-react">{t9}</div>;
    $[15] = content.sourceInteraction;
    $[16] = t9;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  let t11;
  if ($[18] !== content.blockId || $[19] !== t10 || $[20] !== t5) {
    t11 = <section className={styles.gallerySection} data-cms-block-id={t2}>{t5}{t10}</section>;
    $[18] = content.blockId;
    $[19] = t10;
    $[20] = t5;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  return t11;
}
function _temp2(card) {
  return <div className={styles.carouselSlide} key={`${card.title}-${card.subtitle}`}><a href={card.href} className={styles.galleryCard} data-cms-field="cards"><CmsImage image={card.image} className={styles.galleryImage} /><h4 className={styles.galleryCardTitle}>{card.title}</h4>{" "}<span className={styles.galleryCardSubtitle}>{card.subtitle}</span></a></div>;
}